// parseDescription — turns a feed event's DESCRIPTION text into the
// structured day object every panel renders from. The highest-risk
// regex surface in the site (phase-3 plan): guarded by the golden
// fixture test in src/__tests__/parse-description.test.ts, whose
// fixture is generated by the real Python engine + ICSGenerator.
// Extracted verbatim from main.ts (one-shell decomposition).

// A time plus optional (+1)/(-1) relative-day marker, as emitted by the feed.
export const TIME_PART = '(\\d{2}:\\d{2})(?:\\s*\\(([+-]1)\\))?';
// \s+ not \s{2,}: long names (Krishna Chaturdashi, named Ekadashis) overflow the column padding
const ANGA_RE = new RegExp(`^(Tithi|Nakshatra|Yoga):\\s+(.+?)\\s+${TIME_PART}\\s*[–-]\\s*${TIME_PART}$`);
const WINDOW_RE = new RegExp(`^\\s+(.+?)\\s{2,}${TIME_PART}\\s*[–-]\\s*${TIME_PART}\\s*$`);
const CHOG_RE = new RegExp(`^\\s+${TIME_PART}\\s*[–-]\\s*${TIME_PART}\\s+(.+)$`);

export function parseDescription(description: string) {
  // Verbatim port: internal shape kept dynamic; panels type it on consumption.
  const data: any = {
    meta: '', tithi: null, nakshatra: null, yoga: null, karana: null,
    sunrise: null, sunset: null, moonrise: null, moonset: null,
    auspicious: [], inauspicious: [], choghadiya: [], nightChoghadiya: [],
    eclipse: null, yogas: [], ayanam: null, rituvu: null, special: [],
  };
  let section: string | null = null;
  for (const raw of description.split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) { section = null; continue; }

    let m: RegExpMatchArray | null;
    // Header: "<year> [Nama Samvatsara]  ·  Maasam  ·  Paksham  ·  Vaaram".
    // The " Nama Samvatsara" suffix is the new (devotee-preferred)
    // form; older deployed feeds still have just "<year>" — strip
    // whichever shows up so data.samvatsara is always the bare year.
    if ((m = line.match(/^(.+?)\s+·\s+(.+?) Maasam\s+·\s+(.+?) Paksham\s+·\s+(.+)$/))) {
      const yearRaw = m[1].trim().replace(/\s+Nama Samvatsara$/i, '').replace(/\s+Samvatsara$/i, '');
      data.samvatsara = yearRaw;
      data.maasam = m[2].trim();
      data.paksham = m[3].trim();
      data.vaaram = m[4].trim();
      data.meta = `${yearRaw} Nama Samvatsara · ${m[2]} Maasam · ${m[3]} Paksham · ${m[4].trim()}`;
      continue;
    }
    // Sky: "Sunrise HH:MM  ·  Sunset HH:MM  ·  Moonrise HH:MM  ·  Moonset HH:MM"
    if ((m = line.match(/^Sunrise (\d{2}:\d{2})\s+·\s+Sunset (\d{2}:\d{2})\s+·\s+Moonrise (\d{2}:\d{2})\s+·\s+Moonset (\d{2}:\d{2})$/))) {
      [, data.sunrise, data.sunset, data.moonrise, data.moonset] = m;
      continue;
    }
    // Ayanam / Rituvu: "Ayanam: X  ·  Rituvu: Y"
    if ((m = line.match(/^Ayanam:\s+(.+?)\s+·\s+Rituvu:\s+(.+)$/))) {
      data.ayanam = m[1].trim();
      data.rituvu = m[2].trim();
      continue;
    }
    // Signs: "Solar sign: X  ·  Lunar sign: Y" (solar sign drives Vaikunta Ekadashi naming)
    if ((m = line.match(/^Solar sign:\s+(.+?)\s+·\s+Lunar sign:\s+(.+)$/))) {
      data.solarSign = m[1].trim();
      data.lunarSign = m[2].trim();
      continue;
    }
    // Pancha anga rows
    if ((m = line.match(ANGA_RE))) {
      data[m[1].toLowerCase()] = { name: m[2].trim(), start: m[3], sflag: m[4] || null, end: m[5], eflag: m[6] || null };
      continue;
    }
    if ((m = line.match(/^Karana:\s+(.+)$/))) {
      data.karana = m[1].trim();
      continue;
    }
    // Section headers
    if (line === '─ Auspicious ─') { section = 'auspicious'; continue; }
    if (line === '─ Inauspicious ─') { section = 'inauspicious'; continue; }
    if (line === '─ Choghadiya ─') { section = 'choghadiya'; continue; }
    if (line === '─ Night Choghadiya ─') { section = 'nightChoghadiya'; continue; }
    if (line === '─ Eclipse ─') { section = 'eclipse'; continue; }
    if (line === '─ Special Yogas ─') { section = 'specialyogas'; continue; }
    // Auspicious / inauspicious entries: "  Name           HH:MM – HH:MM (+1)"
    if ((section === 'auspicious' || section === 'inauspicious') && (m = line.match(WINDOW_RE))) {
      data[section].push({ name: m[1].trim(), start: m[2], sflag: m[3] || null, end: m[4], eflag: m[5] || null });
      continue;
    }
    // Choghadiya entries: "  HH:MM – HH:MM  Name"
    if ((section === 'choghadiya' || section === 'nightChoghadiya') && (m = line.match(CHOG_RE))) {
      data[section].push({ start: m[1], end: m[3], name: m[5].trim() });
      continue;
    }
    // Eclipse detail line: "  🌒 Solar Eclipse (Total) — visible/not visible..."
    if (section === 'eclipse' && (m = line.match(/^\s*\S+\s+(Solar|Lunar) Eclipse \((.+?)\)\s*[—-]\s*(.+)$/))) {
      data.eclipse = { kind: m[1], subtype: m[2], visible: !m[3].includes('not visible'), window: null, sutak: null };
      continue;
    }
    if (section === 'eclipse' && data.eclipse && (m = line.match(/^\s+Window:\s+(.+?)\s*[–-]\s*(.+)$/))) {
      data.eclipse.window = { start: m[1].trim(), end: m[2].trim() };
      continue;
    }
    if (section === 'eclipse' && data.eclipse && (m = line.match(/^\s+Sutak:\s+(.+?)\s*[–-]\s*(.+)$/))) {
      data.eclipse.sutak = { start: m[1].trim(), end: m[2].trim() };
      continue;
    }
    // Special yoga entries: "  Yoga Name" (new format)
    if (section === 'specialyogas' && (m = line.match(/^\s+(.+)$/))) {
      data.yogas.push(m[1].trim());
      continue;
    }
    // Backward compat: old "Yogas: X, Y" single-line format
    if ((m = line.match(/^Yogas:\s+(.+)$/))) {
      data.yogas = m[1].trim().split(/,\s*/);
      continue;
    }
    // Trailing special-day line: "⚡ Ekadashi — fasting day  ·  Pradosham"
    if ((m = line.match(/^⚡\s*(.+)$/))) {
      data.special = m[1].split(/\s+·\s+/).map(s => s.trim());
      continue;
    }
  }
  return data;
}


